/**
 * About chests (mockup 34): every tier's odds, what earns it, and the rules.
 *
 * The promise [D-021](../../../docs/DECISIONS.md#d-021) made — *the odds are
 * shown in the app* — kept in full. Everything on it comes from `chest.odds`,
 * which reads the same table the roll uses, so this screen cannot say a number
 * the chests do not keep ([D-151](../../../docs/DECISIONS.md#d-151)).
 */

import { useEffect, useState } from 'react';
import type { Client } from './api.js';
import Sneakers from './Sneakers.js';
import '@capytalk/tokens/blocks/chest.css';

interface Tier {
  readonly tier: string;
  readonly earnedBy: string;
  readonly acornsMin: number;
  readonly acornsMax: number;
  readonly chance: string;
  readonly pool: readonly string[];
  /** Food and eggs, in words. Optional: an older server does not send it. */
  readonly treats?: string;
}

interface Odds {
  readonly tiers: readonly Tier[];
  readonly luck: string;
}

/** The chip classes are the mockup's, and wooden's is `wood`. */
const CHIP: Readonly<Record<string, string>> = { wooden: 'wood' };

const title = (word: string): string => word.charAt(0).toUpperCase() + word.slice(1);

export default function ChestOdds({
  client,
  onClose,
  tier,
}: {
  readonly client: Client;
  readonly onClose: () => void;
  /** The chest the child was looking at, picked out on the ladder. */
  readonly tier?: string;
}) {
  const [odds, setOdds] = useState<Odds | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    // Through `Promise.resolve`: a server older than this app answers `odds`
    // with a bare list, or not at all (D-066), and either reads as "not now".
    Promise.resolve()
      .then(() => client.chest.odds.query())
      .then((answer) => {
        if (!live) return;
        if (Array.isArray((answer as Partial<Odds>).tiers)) setOdds(answer);
        else setFailed(true);
      })
      .catch(() => {
        if (live) setFailed(true);
      });
    return () => {
      live = false;
    };
  }, [client]);

  const fewest = odds === null ? null : Math.min(...odds.tiers.map((t) => t.acornsMin));

  return (
    <section
      className="card"
      style={{ marginTop: 12, width: '100%' }}
      aria-label="About chests"
    >
      <h3>About chests</h3>
      <div style={{ marginTop: 8 }}>
        <Sneakers
          text="Better chests give better clothes. Here is exactly how it works."
          audioRef={null}
          size={44}
          speak={false}
        />
      </div>

      {failed && (
        <p className="tiny" style={{ marginTop: 10 }}>
          I can’t show this just now. Try again later!
        </p>
      )}

      {odds !== null && (
        <>
          <p className="kicker" style={{ marginTop: 14, marginBottom: 8 }}>
            Chance of clothes
          </p>
          <ul className="ladder" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {odds.tiers.map((one) => (
              <li
                key={one.tier}
                className={one.tier === tier ? 'rung on' : 'rung'}
                aria-current={one.tier === tier ? 'true' : undefined}
                // What earns it on a line of its own: squeezed between the chip
                // and the odds it wrapped to four lines on a phone.
                style={{ flexWrap: 'wrap', rowGap: 6 }}
              >
                <span className={`tier ${CHIP[one.tier] ?? one.tier}`}>
                  {title(one.tier)}
                </span>
                <span className="od" style={{ marginLeft: 'auto', textAlign: 'right' }}>
                  {one.chance}
                  <div className="tiny" style={{ fontWeight: 600 }}>
                    {one.pool.map(title).join(' · ')}
                  </div>
                </span>
                <span className="tiny" style={{ flexBasis: '100%', fontWeight: 600 }}>
                  {one.earnedBy}
                </span>
                {one.treats !== undefined && (
                  <span className="tiny" style={{ flexBasis: '100%' }}>
                    <span aria-hidden="true">🐾</span> {one.treats}
                  </span>
                )}
              </li>
            ))}
          </ul>

          <ul style={{ listStyle: 'none', padding: 0, margin: '14px 0 0' }}>
            <li className="lrow">
              <span className="ic" aria-hidden="true">
                🌰
              </span>
              <span className="tx tiny">
                <b>Every chest has acorns</b> — at least {fewest}. Bigger chests have
                more. A chest is never empty.
              </span>
            </li>
            <li className="lrow">
              <span className="ic" aria-hidden="true">
                ✔️
              </span>
              <span className="tx tiny">
                <b>You never get the same thing twice.</b>
              </span>
            </li>
            <li className="lrow">
              <span className="ic" aria-hidden="true">
                ⭐
              </span>
              <span className="tx tiny">{odds.luck}</span>
            </li>
            <li className="lrow" style={{ borderBottom: 0 }}>
              <span className="ic" aria-hidden="true">
                🛍️
              </span>
              <span className="tx tiny">
                All the clothes in chests are <b>also in the shop</b>, so you can just
                save up.
              </span>
            </li>
          </ul>
        </>
      )}

      <button className="btn" type="button" style={{ marginTop: 12 }} onClick={onClose}>
        Got it
      </button>
    </section>
  );
}
